import PropTypes from "prop-types";
import { Fragment, memo, useEffect, useState } from "react";

// Components
import Button from "@/Layouts/components/Buttons/Button";
import GradientHeader from "@/Layouts/components/Basics/GradientHeader";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// Inputs
import DoubleInput from "@/Layouts/components/InputComponents/DoubleInput";
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";

// MUI
import { Divider, Stack, Tooltip, Typography } from "@mui/material";

// Icons
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import RemoveCircleRoundedIcon from "@mui/icons-material/RemoveCircleRounded";
import InfoIcon from "@Washouts/assets/icons/info-grey.svg";

const FunctionalInput = (props) => <InputFormExtension {...props} />;

function ColumnHeaderContent(props) {
	const { title, tooltip } = props;
	return (
		<Stack direction="row">
			<GradientHeader>
				<Typography variant="h1" component="div" className={"tiered-title"}>
					{title || ""}
				</Typography>
				{tooltip && (
					<Stack direction={"row"}>
						<Tooltip title={tooltip?.title || ""} placement={tooltip?.placement || "right"} arrow>
							<img src={InfoIcon} alt="Information icon" />
						</Tooltip>
					</Stack>
				)}
			</GradientHeader>
		</Stack>
	);
}
ColumnHeaderContent.propTypes = {
	title: PropTypes.string,
	tooltip: PropTypes.object,
};

const getInputProperties = ({
	columnId,
	input,
	identifier,
	positions,
	useErrors,
	callbacks,
	disabled = false,
	isLastError = false,
	readOnly = false,
}) => {
	if (!input || !identifier || !positions) {
		return null;
	}

	return {
		disabled,
		readOnly,
		value: input?.[identifier]?.["value"] || 0,
		defaultType: input?.[identifier]?.["input"],
		error: useErrors?.[positions?.row]?.[identifier] || isLastError,
		onChangeType: (value, inputType) =>
			callbacks?.onChangeType?.({ columnId, row: positions.row, column: positions.column }, identifier, value, inputType),
		onActionUpdate: (value, inputType) =>
			callbacks?.onActionUpdate?.({ columnId, row: positions.row, column: positions.column }, identifier, value, inputType),
	};
};

const isInputDisabled = ({ isCommonDisabled = false, isRowDisabled = false, isMandatoryDisabled = false }) => {
	const isInPlaceDisabled = isCommonDisabled && isRowDisabled;
	return isInPlaceDisabled || isMandatoryDisabled;
};
const isInputDashed = ({ isDisabled = false, isCurrentInput = false }) => isDisabled && isCurrentInput;

function DoubleInputInstance(props) {
	const { columnId, order, errors, disableCommonInputs, positions, properties, input, readOnly, callbacks } = props;

	const useErrors = errors?.get(columnId) || [];
	const mandatoryDisabled = positions?.mandatoryDisabledValues;

	const hasPlus = Boolean(properties?.separatorText === "+");

	const separator = {
		label: properties?.separatorText || "to",
		px: hasPlus ? "8px" : "5px",
		py: "0px",
	};

	const from = order?.[0] || "from";
	const to = order?.[1] || "to";

	// Disabled behaviors
	const mandatoryFromDisabled = mandatoryDisabled?.[from] || false;
	const mandatoryToDisabled = mandatoryDisabled?.[to] || false;
	const fromDisabled = isInputDisabled({
		isCommonDisabled: disableCommonInputs,
		isRowDisabled: positions?.firstRow,
		isMandatoryDisabled: mandatoryFromDisabled,
	});
	const toDisabled = isInputDisabled({
		isCommonDisabled: disableCommonInputs,
		isRowDisabled: positions?.lastRow,
		isMandatoryDisabled: mandatoryToDisabled,
	});

	const fromDashed = isInputDashed({ isDisabled: fromDisabled, isCurrentInput: positions?.firstRow });
	const toDashed = isInputDashed({ isDisabled: toDisabled, isCurrentInput: positions?.lastRow });

	const defaultFromValues = getInputProperties({
		columnId,
		input,
		positions,
		useErrors,
		callbacks,
		identifier: from,
		disabled: fromDisabled,
		readOnly: fromDisabled || readOnly,
	});

	const defaultToValues = getInputProperties({
		columnId,
		input,
		positions,
		useErrors,
		callbacks,
		identifier: to,
		disabled: toDisabled,
		readOnly: toDisabled || readOnly,
	});

	// Add dashed properties
	properties.firstInput.data = properties.firstInput.data.map((inputProps) => ({ ...inputProps, dashed: fromDashed }));
	properties.secondInput.data = properties.secondInput.data.map((inputProps) => ({ ...inputProps, dashed: toDashed }));

	const sendProperties = {
		separator,
		firstInput: { ...properties?.firstInput, ...defaultFromValues },
		secondInput: { ...properties?.secondInput, ...defaultToValues },
	};

	return <DoubleInput {...sendProperties} />;
}
DoubleInputInstance.propTypes = {
	columnId: PropTypes.string,
	order: PropTypes.array,
	errors: PropTypes.object,
	disableCommonInputs: PropTypes.bool,
	positions: PropTypes.object,
	properties: PropTypes.object,
	input: PropTypes.object,
	readOnly: PropTypes.bool,
	callbacks: PropTypes.shape({
		onChangeType: PropTypes.func,
		onActionUpdate: PropTypes.func,
	}),
};

function InputFormInstance(props) {
	const { columnId, order, errors, disableCommonInputs, positions, properties, input, lastCommissionError, readOnly, callbacks } = props;

	const useErrors = errors?.get(columnId) || [];
	const mandatoryDisabled = positions?.mandatoryDisabledValues;

	const index = order?.[0] || null;
	if (!index) {
		console.error("You need to provide an 'index' in your InputForm definition.");
		return <></>;
	}

	// Disabled behaviors
	const mandatoryInputDisabled = mandatoryDisabled?.[index] || false;

	const firstDisabled = isInputDisabled({
		isCommonDisabled: disableCommonInputs,
		isRowDisabled: positions?.firstRow,
		isMandatoryDisabled: mandatoryInputDisabled,
	});
	const lastDisabled = isInputDisabled({
		isCommonDisabled: disableCommonInputs,
		isRowDisabled: positions?.lastRow,
		isMandatoryDisabled: mandatoryInputDisabled,
	});

	const isLastError = lastCommissionError && positions?.lastRow;
	const firstDashed = isInputDashed({ isDisabled: firstDisabled, isCurrentInput: positions?.firstRow });
	const lastDashed = isInputDashed({ isDisabled: lastDisabled, isCurrentInput: positions?.lastRow });

	const defaultFromValues = getInputProperties({
		input,
		positions,
		useErrors,
		callbacks,
		isLastError,
		columnId: positions.columnId,
		identifier: index,
		disabled: firstDisabled || lastDisabled,
		readOnly: firstDisabled || lastDisabled || readOnly,
	});

	const isDefaultValueAllowed = Boolean(properties.input?.defaultValueAll) || positions.lastRow;

	// Add dashed properties
	properties.input.data = properties.input.data.map((inputProps) => ({ ...inputProps, dashed: firstDashed || lastDashed }));

	const sendProperties = {
		...properties.input,
		...defaultFromValues,
		isDefaultValueAllowed,
	};

	return <FunctionalInput {...sendProperties} />;
}
InputFormInstance.propTypes = {
	columnId: PropTypes.string,
	order: PropTypes.array,
	errors: PropTypes.object,
	disableCommonInputs: PropTypes.bool,
	positions: PropTypes.object,
	properties: PropTypes.object,
	input: PropTypes.object,
	readOnly: PropTypes.bool,
	callbacks: PropTypes.shape({
		onChangeType: PropTypes.func,
		onActionUpdate: PropTypes.func,
	}),
};

function InstanceComponent(props) {
	const {
		type = null,
		order = [],
		columnId = "default",
		errors = null,
		disableCommonInputs = false,
		positions = null,
		properties = null,
		input = null,
		readOnly = false,
		lastCommissionError = false,
		callbacks = {
			onChangeType: () => {},
			onActionUpdate: () => {},
		},
	} = props;

	switch (type) {
		case "DoubleInput": {
			return (
				<DoubleInputInstance
					columnId={columnId}
					order={order}
					errors={errors}
					disableCommonInputs={disableCommonInputs}
					positions={positions}
					properties={properties}
					input={input}
					readOnly={readOnly}
					callbacks={callbacks}
				/>
			);
		}
		case "InputForm": {
			return (
				<InputFormInstance
					columnId={columnId}
					order={order}
					errors={errors}
					disableCommonInputs={disableCommonInputs}
					positions={positions}
					properties={properties}
					input={input}
					lastCommissionError={lastCommissionError}
					readOnly={readOnly}
					callbacks={callbacks}
				/>
			);
		}
		default:
			return <></>;
	}
}
InstanceComponent.propTypes = {
	type: PropTypes.string,
	order: PropTypes.array,
	columnId: PropTypes.string,
	errors: PropTypes.object,
	disableCommonInputs: PropTypes.bool,
	positions: PropTypes.object,
	properties: PropTypes.object,
	input: PropTypes.object,
	lastCommissionError: PropTypes.bool,
	readOnly: PropTypes.bool,
	callbacks: PropTypes.shape({
		onChangeType: PropTypes.func,
		onActionUpdate: PropTypes.func,
	}),
};

function ActionButton(props) {
	return <Button {...props} />;
}

const MemoActionButton = memo(ActionButton);
const ColumnHeader = memo(ColumnHeaderContent);
const RowItem = memo(InstanceComponent);

const getTiersButtonsAreaWidth = (columns = []) => {
	let area = 0;

	for (const column of columns) {
		const maxWidth = column?.maxWidth || null;
		const parsedMaxWidth = String(maxWidth).includes("px") ? Number(String(maxWidth).replace("px", "")) : maxWidth || 0;
		area += Number(parsedMaxWidth);
	}

	return area ? `${area}px` : "100%";
};

const getLeftDividerPosition = (spacing = 1) => {
	const position = spacing * 4;
	return `-${position}px`;
};

export default function TierItemsLayout(props) {
	const {
		columns = [],
		rows = [],
		disabledRowsAttributes = [],
		errors = new Map(),
		errorsMessages = [],
		minimumRows = 1,
		maximumRows = 50,
		lastCommissionError = false,
		readOnly = false,
		hasDivider = true,
		containerSx = null,
		columnSpacing = 3,
		// dividerSx = { paddingTop: "18px !important", width: "2px" },
		buttonsContainerProps = {
			direction: "row",
			justifyContent: "center",
			alignItems: "center",
			spacing: 2,
			mt: 0,
		},
		addButtonText = "Add Tier",
		deleteButtonText = "Remove Tier",
		addButtonDisabled = false,
		deleteButtonDisabled = false,
		onActionAdd = () => {},
		onActionDelete = () => {},
		addButtonProps = {
			variant: "contained",
			classes: "tier-btn add-tier-btn",
			bgColor: "var(--primary-color)",
			endIcon: <AddCircleRounded />,
		},
		deleteButtonProps = {
			variant: "contained",
			classes: "tier-btn remove-tier-btn",
			bgColor: "var(--gray-blue-color)",
			endIcon: <RemoveCircleRoundedIcon />,
		},
	} = props;

	// States
	const [innerColumns, setInnerColumns] = useState([]);
	const [innerRows, setInnerRows] = useState([]);

	const disabledRemove = innerRows?.length <= minimumRows || deleteButtonDisabled;
	const disabledAdd = innerRows?.length >= maximumRows || addButtonDisabled;

	useEffect(() => {
		setInnerColumns(columns);
		setInnerRows(rows);

		return () => {
			setInnerColumns([]);
			setInnerRows([]);
		};
	}, [columns, rows]);

	return (
		<Fragment>
			<Stack direction={"row"} spacing={columnSpacing} sx={containerSx}>
				{innerColumns.map((column, index) => {
					const divider = Boolean(hasDivider && index) && !column?.avoidDivider;
					return (
						<Stack key={`${column?.title}-${index}`} alignItems={"center"} position={"relative"} sx={column?.sx || null}>
							{divider && (
								<Divider
									className="!border-disabled"
									textAlign="center"
									orientation="vertical"
									variant="middle"
									style={{
										maxHeight: "20px",
										marginTop: "8px",
										position: "absolute",
										left: getLeftDividerPosition(columnSpacing),
									}}
								/>
							)}
							<ColumnHeader divider={divider} title={column?.title} tooltip={column?.tooltip} />
							<Stack justifyContent={"center"} alignItems={"center"} spacing={1}>
								{innerRows.map((input, key) => {
									const mandatoryDisabledValues = disabledRowsAttributes?.[key] || null;
									return (
										<Stack key={`${index}_${key}`}>
											{Boolean(column?.appendBefore) && <Stack>{column.appendBefore}</Stack>}
											<RowItem
												{...column?.component}
												input={input}
												columnId={column?._id}
												positions={{
													column: index,
													firstColumn: !index,
													lastColumn: innerColumns.length - 1 === index,
													row: key,
													firstRow: !key,
													lastRow: innerRows.length - 1 === key,
													mandatoryDisabledValues,
												}}
												errors={errors}
												lastCommissionError={lastCommissionError}
												readOnly={readOnly}
											/>
										</Stack>
									);
								})}
							</Stack>
						</Stack>
					);
				})}
			</Stack>
			{Boolean(errorsMessages.length) && (
				<Stack mt={1}>
					{errorsMessages.map((message, key) => (
						<TierAlertError key={`error_tier_items_${message}_${key}`} text={message} />
					))}
				</Stack>
			)}
			<Stack mt={2.5}>
				<Stack {...buttonsContainerProps} width={getTiersButtonsAreaWidth(innerColumns)}>
					<MemoActionButton
						{...deleteButtonProps}
						text={deleteButtonText}
						disabled={readOnly || disabledRemove}
						onClick={onActionDelete}
					/>
					<MemoActionButton {...addButtonProps} text={addButtonText} disabled={readOnly || disabledAdd} onClick={onActionAdd} />
				</Stack>
			</Stack>
		</Fragment>
	);
}
TierItemsLayout.propTypes = {
	columns: PropTypes.array,
	rows: PropTypes.array,
	disabledRowsAttributes: PropTypes.array,
	errors: PropTypes.object,
	errorsMessages: PropTypes.array,
	minimumRows: PropTypes.number,
	maximumRows: PropTypes.number,
	lastCommissionError: PropTypes.bool,
	readOnly: PropTypes.bool,
	hasDivider: PropTypes.bool,
	containerSx: PropTypes.object,
	columnSpacing: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	dividerSx: PropTypes.object,
	buttonsContainerProps: PropTypes.object,
	addButtonText: PropTypes.string,
	deleteButtonText: PropTypes.string,
	addButtonDisabled: PropTypes.bool,
	deleteButtonDisabled: PropTypes.bool,
	onActionAdd: PropTypes.func,
	onActionDelete: PropTypes.func,
	addButtonProps: PropTypes.object,
	deleteButtonProps: PropTypes.object,
};
